import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { useTranslation } from "react-i18next"
import api from "../api"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { AdminRole, ButtonStatus } from "../common/constants"
import MainContext from "../common/MainContext"
import ImageCropper from "../components/ImageCropper"
import { auth } from "../firebase"
import typo from "../typography.module.css"
import { capitalize } from "../utils"
import Button from "./Button"
import Dialog from "./dialogs/Dialog"
import styles from "./EditProfile.module.css"
import TextInput from "./TextInput"
import Select from "./Select"

const EditProfile = ({ onBack = () => { }, tagsList = [] }) => {
  const { t } = useTranslation();
  const context = useContext(MainContext)

  const [profilePic, setProfilePic] = useState(null)
  const [name, setName] = useState(context.user?.name ?? '')
  const [surname, setSurname] = useState(context.user?.surname ?? '')
  const [oldPassword, setOldPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [repeatNewPassword, setRepeatNewPassword] = useState("")
  const [error, setError] = useState("")
  const [hasChanges, setHasChanges] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [imgFile, setImgFile] = useState(null)
  const [cropMode, setCropMode] = useState(false)
  const [alert, setAlert] = useState(null)

  const [savingStatus, setSavingStatus] = useState(null)

  const dropzoneRef = useRef(null)

  const roleOptions = useMemo(() => {
    return [
      { id: 0, name: "Main", value: AdminRole.Main },
      { id: 1, name: "Sales", value: AdminRole.Sales },
    ]
  }, [])
  const [role, setRole] = useState(roleOptions.find(r => r.value === context.user?.role) ?? roleOptions[0])

  const isCompanyAdmin = useMemo(() => {
    return Boolean(context.user?.company_id ?? context.user?.company?.id ?? context.user?.company)
  }, [context.user])

  useEffect(() => {
    setError(
      newPassword && repeatNewPassword && newPassword !== repeatNewPassword ?
        t('errors.passwordNotEquals') : ""
    )
  }, [newPassword, repeatNewPassword])


  useEffect(() => {
    if (context.user) {
      setHasChanges(
        (name !== context.user.name) ||
        (surname !== context.user.surname) ||
        (!isCompanyAdmin && role && roleOptions[role.id].value !== context.user.role) ||
        (newPassword !== '' && repeatNewPassword !== '' && newPassword === repeatNewPassword) ||
        ((profilePic && profilePic !== context.user.profilePic) ?? false)
      )
    }
  }, [context.user, name, surname, profilePic, newPassword, repeatNewPassword, role, roleOptions, isCompanyAdmin])

  const saveChanges = async () => {
    setSavingStatus(ButtonStatus.Loading)
    try {
      const formData = new FormData()
      formData.append("name", name)
      formData.append("surname", surname)
      if (!isCompanyAdmin && role) {
        formData.append("role", roleOptions[role.id].value)
      }
      if (profilePic) {
        let blob = await fetch(profilePic).then(r => r.blob());
        formData.append("picture", blob, `${context.user.id}-picture.jpg`);
      }

      try {
        const user = await api.put("/admin", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        if (user) {

          context.setUser(user)
          setProfilePic(null)
        }
      } catch (e) {
        throw new Error("Non è stato possibile salvare le modifiche.")
      }

      if (newPassword !== '' && repeatNewPassword !== '' && (newPassword === repeatNewPassword)) {
        const credential = EmailAuthProvider.credential(
          auth.currentUser.email,
          oldPassword
        )
        try {
          await reauthenticateWithCredential(auth.currentUser, credential)
        }
        catch (error) {
          throw new Error('Autenticazione fallita.')
        }
        await updateUserPassword()
      }
      setOldPassword('')
      setNewPassword('')
      setRepeatNewPassword('')
      setDialogOpen(true)
      setAlert({ title: t('profile.saved'), message: t('profile.changesSaved') })
      setSavingStatus(ButtonStatus.Success)
    } catch (e) {
      console.error(e)
      setError(e.message)
      setSavingStatus(ButtonStatus.Error)
    }
  }

  const updateUserPassword = async () => {
    try {
      await updatePassword(auth.currentUser, newPassword)
    }
    catch (error) {
      setError(error.message)
    }
  }


  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}><div><h1>Modifica profilo</h1><p>Aggiorna i tuoi dati e le impostazioni di accesso.</p></div><Button appearance="subtle" onClick={onBack}>Annulla</Button></div>
      <div className={styles.content}>
        <div className={styles.leftColumn}>
          <div className={styles.admin}>
            <img className={styles.profilePic} src={(profilePic || context.user?.picture) ?? UserPlaceholder} alt='' />
            <div className={styles.username}>{`${context.user?.name} ${context.user?.surname}`}</div>
            <Button
              appearance="subtle" accentColor="var(--primary)"
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              <EditIcon />
              Cambia foto
            </Button>
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{t("profile.editTitle")}</div>
            <div className={styles.inputs}>
              <div className={styles.row}>
                <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
              </div>
              <div className={styles.row}>
                {!isCompanyAdmin &&
                  <div className={styles.field}><span className={styles.fieldLabel}>Ruolo</span><Select appearance="modern" portal selected={role} options={roleOptions} onSelect={(r) => setRole(r)} /></div>
                }
              </div>
            </div>
            <div className={styles.cardTitle}>{t("profile.changePassword")}</div>
            <div className={styles.inputs}>
              <TextInput appearance="modern" value={oldPassword} type="password" label={capitalize(t('profile.oldPassword'))} placeholder={capitalize(t('profile.oldPassword'))} onKeyUp={v => setOldPassword(v)} />
              <TextInput appearance="modern" value={newPassword} type="password" label={capitalize(t('profile.newPassword'))} placeholder={capitalize(t('profile.newPassword'))} onKeyUp={v => setNewPassword(v)} />
              <TextInput appearance="modern" value={repeatNewPassword} type="password" label={capitalize(t('profile.repeatNewPassword'))} placeholder={capitalize(t('profile.repeatNewPassword'))} onKeyUp={v => setRepeatNewPassword(v)} />
              {
                error &&
                <span className={typo.caption} style={{ color: "var(--secondary)" }}>
                  {error}
                </span>
              }
            </div>
            <div style={{ alignSelf: "flex-end" }}>
              <Button
                status={savingStatus}
                accentColor={"var(--primary)"}
                style={{ padding: ".65rem 1rem", borderRadius: 8, fontSize: ".75rem" }}
                disabled={hasChanges === false || savingStatus === ButtonStatus.Loading}
                onClick={saveChanges}
              >
                Salva modifiche
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Dialog appearance="modern"
        open={dialogOpen}
        style={{
          width: "100%",
          maxWidth: "512px"
        }}
        onClose={() => {
          setDialogOpen(false)
          setCropMode(false)
          setAlert(null)
          if (alert) {
            onBack()
          }
        }}
        title={alert ? alert.title : "Immagine del profilo"}>
        <div className={styles.dialog}>
          {!alert &&
            <>
              {!cropMode &&
                <Dropzone
                  onDrop={(files) => {
                    if (files[0]) {
                      setImgFile(files[0])
                      setCropMode(true)
                    }
                  }}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'image/heic': []
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section style={{ display: 'flex', width: '100%' }}>
                      <div {...getRootProps()} style={{ display: 'flex', width: '100%' }}>
                        <input {...getInputProps()} ref={dropzoneRef} />
                        <div className={styles.editPictureDropzone}>
                          <InsertImageIllustration></InsertImageIllustration>
                          <div className={styles.editPictureLabel}>
                            {t('profile.dropImage')}
                          </div>
                          <Button
                            style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                            appearance="subtle" accentColor="var(--primary)"
                            onClick={() => dropzoneRef.current?.click()}
                          >
                            Scegli immagine
                          </Button>
                        </div>
                      </div>
                    </section>
                  )}
                </Dropzone>
              }
              {cropMode &&
                <>
                  <ImageCropper
                    src={URL.createObjectURL(imgFile)}
                    onSave={(image) => {
                      setProfilePic(image)
                      setCropMode(false)
                      setDialogOpen(false)
                    }}
                    onCancel={() => { setCropMode(false) }}
                    compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                  >
                  </ImageCropper>
                </>
              }
            </>
          }
          {alert &&
            <div className={styles.editPictureLabel} style={{ justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              {alert.message}
            </div>
          }
        </div>
      </Dialog>
    </div>
  )
}

export default EditProfile
